import React from "react";
import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import { padLeadingZeroes } from "utils/utilityFunctions";

// Define styles for PDF
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontFamily: "Helvetica",
        fontSize: 10,
        color: "#333",
        backgroundColor: "#fff",
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 30,
        borderBottom: "2 solid #3B0069",
        paddingBottom: 15,
    },
    titleContainer: {
        flexDirection: "column",
    },
    title: {
        fontSize: 24,
        fontWeight: "bold",
        color: "#3B0069",
        textTransform: "uppercase",
        letterSpacing: 1,
    },
    subtitle: {
        fontSize: 12,
        color: "#666",
        marginTop: 5,
    },
    orgDetails: {
        textAlign: "right",
        width: "40%",
    },
    orgName: {
        fontSize: 14,
        fontWeight: "bold",
        color: "#3B0069",
        marginBottom: 4,
    },
    orgAddress: {
        fontSize: 9,
        color: "#666",
        // maxWidth: 100,
        textAlign: "right",
        lineHeight: 1.4,
    },
    section: {
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: "bold",
        backgroundColor: "#F3F4F6",
        padding: 6,
        color: "#3B0069",
        marginBottom: 10,
        textTransform: "uppercase",
    },
    infoGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 10,
    },
    infoItem: {
        width: "50%",
        marginBottom: 8,
        paddingRight: 10,
    },
    label: {
        fontSize: 9,
        color: "#666",
        marginBottom: 2,
        textTransform: "uppercase",
    },
    value: {
        fontSize: 10,
        fontWeight: "bold",
        color: "#1F2937",
    },
    table: {
        marginTop: 5,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#F9FAFB",
        borderBottom: "1 solid #E5E7EB",
        padding: 8,
        fontWeight: "bold",
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "0.5 solid #E5E7EB",
        padding: 8,
        alignItems: "center",
    },
    tableColItem: { width: "25%" },
    tableColDesc: { width: "35%" },
    tableColQty: { width: "10%", textAlign: "right" },
    tableColUnit: { width: "10%", textAlign: "center" },
    tableColRate: { width: "10%", textAlign: "right" },
    tableColTotal: { width: "10%", textAlign: "right" },

    tableHeaderText: {
        fontSize: 9,
        color: "#374151",
        fontWeight: "bold",
    },
    tableCell: {
        fontSize: 9,
        color: "#4B5563",
    },
    grandTotalRow: {
        flexDirection: "row",
        borderTop: "1.5 solid #3B0069",
        padding: 8,
        marginTop: 5,
    },
    grandTotalLabel: {
        width: "90%",
        textAlign: "right",
        paddingRight: 10,
        fontSize: 11,
        fontWeight: "bold",
        color: "#3B0069",
    },
    grandTotalValue: {
        // width: "10%",
        textAlign: "right",
        fontSize: 11,
        fontWeight: "bold",
        color: "#3B0069",
    },
    milestoneRow: {
        flexDirection: "row",
        borderBottom: "0.5 solid #E5E7EB",
        padding: 8,
    },
    milestoneName: { width: "30%" },
    milestoneDesc: { width: "40%" },
    milestonePerc: { width: "10%", textAlign: "center" },
    milestoneValue: { width: "20%", textAlign: "right" },

    termsContainer: {
        marginTop: 20,
        padding: 10,
        backgroundColor: "#F9FAFB",
        borderRadius: 4,
    },
    termsTitle: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 5,
        color: "#374151",
    },
    termsText: {
        fontSize: 8,
        color: "#6B7280",
        lineHeight: 1.5,
    },
    footer: {
        position: "absolute",
        bottom: 30,
        left: 40,
        right: 40,
        textAlign: "center",
        borderTop: "0.5 solid #E5E7EB",
        paddingTop: 10,
        fontSize: 8,
        color: "#999",
    }
});

const formatCurrency = (amount) => {
    return `Rs. ${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch (e) {
        return dateStr;
    }
};

export function WorkOrderPDFDocument({ details, contractor, organization }) {
    const currentVersion = details.versions.length ? Math.max(...details.versions.map(v => v.version)) : 1;
    const grandTotal = details.scopeItems.reduce((sum, item) => sum + item.totalCost, 0);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header Section */}
                <View style={styles.header}>
                    <View style={styles.titleContainer}>
                        <Text style={styles.title}>Work Order</Text>
                        <Text style={styles.subtitle}>#WO_{padLeadingZeroes(details.id, 3)}</Text>
                    </View>
                    <View style={styles.orgDetails}>
                        <Text style={styles.orgName}>{organization?.organization_name || "Motion Builders LLP"}</Text>
                        <Text style={styles.orgAddress}>{organization?.business_address || "FD-172, Salt Lake Sector -III"}</Text>
                        <Text style={styles.orgAddress}>{organization?.state}, {organization?.pincode}</Text>
                        {organization?.user_email && <Text style={styles.orgAddress}>{organization?.user_email}</Text>}
                    </View>
                </View>

                {/* Overview Grid */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>General Information</Text>
                    <View style={styles.infoGrid}>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Contractor</Text>
                            <Text style={styles.value}>{contractor?.name}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Project</Text>
                            <Text style={styles.value}>{details.projectName}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Contract Type</Text>
                            <Text style={styles.value}>{details.contractType?.replace('_', ' ')}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Payment Structure</Text>
                            <Text style={styles.value}>{details.paymentStructure?.replace('_', ' ')}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Start Date</Text>
                            <Text style={styles.value}>{formatDate(details.startDate)}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>End Date</Text>
                            <Text style={styles.value}>{formatDate(details.endDate)}</Text>
                        </View>
                    </View>
                </View>

                {/* Scope of Work Table */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Scope of Work</Text>
                    <View style={styles.table}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.tableColItem, styles.tableHeaderText]}>Item</Text>
                            <Text style={[styles.tableColDesc, styles.tableHeaderText]}>Description</Text>
                            <Text style={[styles.tableColQty, styles.tableHeaderText]}>Qty</Text>
                            <Text style={[styles.tableColUnit, styles.tableHeaderText]}>Unit</Text>
                            <Text style={[styles.tableColRate, styles.tableHeaderText]}>Rate</Text>
                            <Text style={[styles.tableColTotal, styles.tableHeaderText]}>Total</Text>
                        </View>
                        {details.scopeItems.map((item, index) => (
                            <View key={item.id || index} style={styles.tableRow}>
                                <Text style={[styles.tableColItem, styles.tableCell]}>{item.item}</Text>
                                <Text style={[styles.tableColDesc, styles.tableCell]}>{item.description}</Text>
                                <Text style={[styles.tableColQty, styles.tableCell]}>{Number(item.quantity).toLocaleString()}</Text>
                                <Text style={[styles.tableColUnit, styles.tableCell]}>{item.unit}</Text>
                                <Text style={[styles.tableColRate, styles.tableCell]}>{Number(item.unitCost).toLocaleString()}</Text>
                                <Text style={[styles.tableColTotal, styles.tableCell, { fontWeight: "bold" }]}>{Number(item.totalCost).toLocaleString()}</Text>
                            </View>
                        ))}
                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>Grand Total</Text>
                            <Text style={styles.grandTotalValue}>{formatCurrency(grandTotal)}</Text>
                        </View>
                    </View>
                </View>

                {/* Payment Milestones (Conditional) */}
                {details.paymentStructure === "MILESTONE_BASED" && details.milestones?.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Payment Milestones</Text>
                        <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={[styles.milestoneName, styles.tableHeaderText]}>Milestone</Text>
                                <Text style={[styles.milestoneDesc, styles.tableHeaderText]}>Description</Text>
                                <Text style={[styles.milestonePerc, styles.tableHeaderText]}>%</Text>
                                <Text style={[styles.milestoneValue, styles.tableHeaderText]}>Value</Text>
                            </View>
                            {details.milestones.map((m, index) => (
                                <View key={m.id || index} style={styles.milestoneRow}>
                                    <Text style={[styles.milestoneName, styles.tableCell]}>{m.name}</Text>
                                    <Text style={[styles.milestoneDesc, styles.tableCell]}>{m.description}</Text>
                                    <Text style={[styles.milestonePerc, styles.tableCell]}>{Number(m.percentage).toFixed(1)}%</Text>
                                    <Text style={[styles.milestoneValue, styles.tableCell]}>{formatCurrency(m.value)}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                )}

                {/* Terms and Conditions */}
                <View style={styles.termsContainer}>
                    <Text style={styles.termsTitle}>Terms & Conditions</Text>
                    <Text style={styles.termsText}>{details.terms || "Standard contract terms apply."}</Text>
                    {details.paymentTerms && (
                        <>
                            <Text style={[styles.termsTitle, { marginTop: 10 }]}>Payment Terms</Text>
                            <Text style={styles.termsText}>{details.paymentTerms}</Text>
                        </>
                    )}
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text>This is a computer-generated document and does not require a physical signature.</Text>
                    <Text>Generated on {new Date().toLocaleString("en-IN")} via SiteMaster</Text>
                </View>
            </Page>
        </Document>
    );
}
